import { FlexColumnSpaced, FlexRowSpacedRightAligned, Padded } from '@fox/layout';
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { Button } from '../button';
import { Dialog } from '../mui-dialog';
import { useClickToDismissContext } from '../popover';

export const useLatest = <T extends unknown>(value: T) => {
	var ref = useRef(value);
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref;
};

// context to
export const useBeforeunload = (handler = (e: BeforeUnloadEvent) => {}) => {
	if (process.env.NODE_ENV !== 'production' && typeof handler !== 'function') {
		throw new TypeError(`Expected "handler" to be a function, not ${typeof handler}.`);
	}

	const handlerRef = useLatest(handler);

	useEffect(() => {
		const handleBeforeunload = (event: BeforeUnloadEvent) => {
			let returnValue: any;

			if (typeof handlerRef.current === 'function') {
				returnValue = handlerRef.current(event);
			}

			if (event.defaultPrevented) {
				event.returnValue = '';
			}

			if (typeof returnValue === 'string') {
				event.returnValue = returnValue;
				return returnValue;
			}
		};

		window.addEventListener('beforeunload', handleBeforeunload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeunload);
		};
	}, [handlerRef]);
};

export function useImperitiveDirtyState() {
	const api = useContext(DirtyContext);

	return api;
}

/** @deprecated use DirtyState component directly */
export function useDirtyState(key: string, dirtyMessage: string | '') {
	const { setDirty } = useImperitiveDirtyState() ?? {};
	useEffect(() => setDirty?.(key, dirtyMessage), [dirtyMessage, key, setDirty]);
}

type DirtyApi = {
	/** The message can be an empty string, in which case it is treated as clean.
	 * @param key A unique key to identify this instance of useDirtyState
	 * @param message The message to display on the unload events
	 */
	setDirty(key: string, message: string): void;
};

const DirtyContext = createContext<DirtyApi | undefined>(undefined);

function PromptModal({
	message,
	shouldBlock,
	onConfirm
}: {
	message: string;
	shouldBlock: boolean | (() => boolean);
	onConfirm?: () => void;
}) {
	const blocker = useBlocker(shouldBlock ?? !!message);
	return (
		<>
			{blocker.state === 'blocked' ? (
				<Dialog open={true}>
					<Padded>
						<FlexColumnSpaced>
							{message}
							<FlexRowSpacedRightAligned>
								<Button
									variant="secondary"
									onClick={() => {
										blocker.reset();
									}}
								>
									Cancel
								</Button>
								<Button
									variant="primary"
									onClick={() => {
										onConfirm?.();
										blocker.proceed();
									}}
								>
									Continue
								</Button>
							</FlexRowSpacedRightAligned>
						</FlexColumnSpaced>
					</Padded>
				</Dialog>
			) : null}
		</>
	);
}

/** The preferred component to mount to handle dirty state concerns - simply flag dirty with a message and it will prevent soft (router) and hard (F5) navigation */
export const DirtyState: React.FC<{ dirty: boolean | (() => boolean); message: string; onConfirm?: () => void }> = ({
	dirty,
	message,
	onConfirm
}) => {
	const ctd = useClickToDismissContext();
	useEffect(() => {
		const handleBeforeunload = (event: BeforeUnloadEvent) => {
			const currentDirty = typeof dirty === 'function' ? dirty() : dirty;
			if (!currentDirty) return '';

			event.preventDefault();

			return (event.returnValue = message);
		};

		if (dirty) {
			ctd.childPortalOpened(`dirtyState_${message}`, () => {
				const currentDirty = typeof dirty === 'function' ? dirty() : dirty;
				if (!currentDirty) return true;

				return confirm(message);
			});
		}

		window.addEventListener('beforeunload', handleBeforeunload);

		return () => {
			if (dirty) ctd.childPortalClosed(`dirtyState_${message}`);
			window.removeEventListener('beforeunload', handleBeforeunload);
		};
	}, [dirty, message]);

	if (!dirty) return null;

	return (
		<PromptModal
			message={message}
			shouldBlock={() => {
				const isDirty = typeof dirty === 'function' ? dirty() : dirty;
				return isDirty;
			}}
			onConfirm={onConfirm}
		/>
	);
};
